import {forward, guard, merge, sample} from 'effector';
import i18next from 'i18next';
import {toast} from 'react-toastify';

import {RestError, showRestError} from 'core/errors';
import {history} from 'core/history';

import {ROUTE} from 'core/routes';

import {tableStateChanged} from '../table';

import {
    data$,
    dataChanged,
    dataSubmitted,
    editGtinFx,
    formData$,
    formDataChanged,
    formDataReset,
    createProductFormDefaults$,
    createProductFormDefaultsChanged,
    isFormModalOpen$,
    isFormModalOpenChanged,
    isModalOpen$,
    isModalOpenChanged,
    uploadDataFx,
    isRecallModalOpen$,
    isRecallModalOpenChanged,
    recallGtinFx,
    deleteGtinFx,
    setSubmitFx,
    setBlockFx,
    setApproveFx,
    setRecallFx,
    releaseNewVersionFx,
    showRecallApproveModal$,
    showRecallApproveModalChanged,
    recallData$,
    recallDataChanged,
    setPublishFx,
    ProductsGate,
    fetchProductGroupsFx,
    productGroups$,
    productGroupsChanged,
    loadProductCardFileFx,
    loadProductCardTaskId$,
    loadProductCardTaskIdChanged,
    loadProductCardFileStatus$,
    loadProductCardFileStatusChanged,
    loadProductCardStatusPollingStarted,
    loadProductCardStatusPollingStopped,
    loadProductCardStatusPollingReset,
    isProductCardLoadStatusPolling$,
    pollProductCardLoadStatusDelayFx,
    getProductCardLoadStatusFx,
} from './index';

const isStatusPending = (status: string) => status === 'PENDING' || status === 'PROCESSING';

loadProductCardFileStatus$
    .on(loadProductCardFileStatusChanged, (_, payload) => payload)
    .reset(loadProductCardStatusPollingReset);
loadProductCardTaskId$
    .on(loadProductCardTaskIdChanged, (_, payload) => payload)
    .reset(loadProductCardStatusPollingReset);
isProductCardLoadStatusPolling$
    .on(loadProductCardStatusPollingStarted, () => true)
    .on(loadProductCardStatusPollingStopped, () => false)
    .reset(loadProductCardStatusPollingReset);

sample({
    clock: loadProductCardFileFx,
    target: loadProductCardStatusPollingReset,
});

sample({
    clock: loadProductCardFileFx.doneData,
    fn: ({taskId}) => taskId,
    target: [loadProductCardTaskIdChanged, loadProductCardStatusPollingStarted],
});

sample({
    clock: loadProductCardStatusPollingStarted,
    target: getProductCardLoadStatusFx,
});

const receivedLoadStatusWithTask = sample({
    source: loadProductCardTaskId$,
    clock: getProductCardLoadStatusFx.doneData,
    fn: (taskId, statusData) => ({taskId, statusData}),
});

const receivedLoadStatusWhilePolling = guard({
    source: receivedLoadStatusWithTask,
    filter: isProductCardLoadStatusPolling$,
});

const receivedMatchedLoadStatus = guard({
    source: receivedLoadStatusWhilePolling,
    filter: ({taskId, statusData}) => !!taskId && taskId === statusData.taskId,
});

sample({
    clock: receivedMatchedLoadStatus,
    fn: ({statusData}) => statusData,
    target: loadProductCardFileStatusChanged,
});

const inProgressLoadStatusesReceived = guard({
    source: receivedMatchedLoadStatus,
    filter: ({statusData}) => isStatusPending(statusData.status),
});

const completedLoadStatusesReceived = guard({
    source: receivedMatchedLoadStatus,
    filter: ({statusData}) => !isStatusPending(statusData.status),
});

sample({
    clock: inProgressLoadStatusesReceived,
    fn: ({statusData}) => statusData.taskId,
    target: pollProductCardLoadStatusDelayFx,
});

const nextPollTaskPrepared = sample({
    source: loadProductCardTaskId$,
    clock: pollProductCardLoadStatusDelayFx.doneData,
    fn: (taskId, nextTaskId) => ({taskId, nextTaskId}),
});

const nextPollTaskWhenPolling = guard({
    source: nextPollTaskPrepared,
    filter: isProductCardLoadStatusPolling$,
});

const nextPollTaskMatched = guard({
    source: nextPollTaskWhenPolling,
    filter: ({taskId, nextTaskId}) => taskId === nextTaskId,
});

sample({
    clock: nextPollTaskMatched,
    fn: ({nextTaskId}) => nextTaskId,
    target: getProductCardLoadStatusFx,
});

forward({
    from: completedLoadStatusesReceived,
    to: loadProductCardStatusPollingStopped,
});

forward({
    from: ProductsGate.open,
    to: fetchProductGroupsFx,
});

forward({
    from: merge([ProductsGate.close, loadProductCardFileFx.fail]),
    to: loadProductCardStatusPollingReset,
});

guard({
    source: isModalOpenChanged,
    filter: (isOpen) => !isOpen,
    target: loadProductCardStatusPollingReset,
});

sample({
    source: fetchProductGroupsFx.doneData,
    target: productGroupsChanged,
});

productGroups$.on(productGroupsChanged, (_, payload) => payload);

data$.on(dataChanged, (_, payload) => payload).reset(uploadDataFx.done);
isModalOpen$.on(isModalOpenChanged, (_, payload) => payload).reset(uploadDataFx.done);

const updateTriggers = [
    editGtinFx.doneData,
    uploadDataFx.doneData,
    setSubmitFx.doneData,
    setBlockFx.doneData,
    setApproveFx.doneData,
    setRecallFx.doneData,
    setPublishFx.doneData,
    releaseNewVersionFx.doneData,
    deleteGtinFx.doneData,
];

formData$
    .on(formDataChanged, (_, payload) => payload)
    .on(updateTriggers, (_, result) => {
        if (result?.fields) return [result.fields];
    })
    .reset(formDataReset);
createProductFormDefaults$
    .on(createProductFormDefaultsChanged, (_, payload) => payload)
    .on(isFormModalOpenChanged, (state, isOpen) => (isOpen ? state : null))
    .reset(editGtinFx.doneData);
isFormModalOpen$.on(isFormModalOpenChanged, (_, payload) => payload);
isRecallModalOpen$.on(isRecallModalOpenChanged, (_, payload) => payload);
uploadDataFx.fail.watch(({error}) => showRestError(error as RestError));
uploadDataFx.done.watch(() => {
    isModalOpenChanged(false);
    toast.success(i18next.t('pages.registries.uploadSuccess'));
});
loadProductCardFileFx.fail.watch(({error}) => showRestError(error as RestError));
getProductCardLoadStatusFx.fail.watch(({error}) => {
    loadProductCardStatusPollingReset();
    showRestError(error as RestError);
});

recallGtinFx.fail.watch(({error}) => showRestError(error as RestError));
recallGtinFx.done.watch(() => {
    history.push(ROUTE.REGISTRIES_GTIN);
    toast.success(i18next.t('pages.registries.recallSuccess'));
});

editGtinFx.fail.watch(({error}) => showRestError(error as RestError));

deleteGtinFx.fail.watch(({error}) => showRestError(error as RestError));
deleteGtinFx.done.watch(() => {
    isFormModalOpenChanged(false);
    toast.success(i18next.t('pages.registries.deleteSuccess'));
});

setSubmitFx.fail.watch(({error}) => showRestError(error as RestError));
setSubmitFx.done.watch(({params}) => {
    toast.success(
        i18next.t(
            params.status ? 'pages.registries.submitSuccess' : 'pages.registries.rejectSuccess',
        ),
    );
});

setPublishFx.fail.watch(({error}) => showRestError(error as RestError));
setPublishFx.done.watch(() => toast.success(i18next.t('pages.registries.submitSuccess')));

setBlockFx.fail.watch(({error}) => showRestError(error as RestError));
setBlockFx.done.watch(({params}) => {
    toast.success(
        i18next.t(
            params.status ? 'pages.registries.blockSuccess' : 'pages.registries.unblockSuccess',
        ),
    );
});

setApproveFx.fail.watch(({error}) => showRestError(error as RestError));
setApproveFx.done.watch(() => {
    toast.success(i18next.t('pages.registries.publishSuccess'));
});

setRecallFx.fail.watch(({error}) => showRestError(error as RestError));
setRecallFx.done.watch(() => {
    toast.success(i18next.t('pages.registries.recallSuccess'));
});

releaseNewVersionFx.fail.watch(({error}) => showRestError(error as RestError));
releaseNewVersionFx.done.watch(() => {
    toast.success(i18next.t('pages.registries.releaseNewVersionSuccess'));
});

showRecallApproveModal$.on(showRecallApproveModalChanged, (_, payload) => payload);

recallData$.on(recallDataChanged, (_, payload) => payload);

sample({
    clock: dataSubmitted,
    source: data$,
    fn: (data) => data || [],
    target: uploadDataFx,
});

forward({
    from: updateTriggers,
    to: tableStateChanged,
});

guard({
    source: isFormModalOpenChanged,
    filter: (isOpen) => !!isOpen,
    target: formDataReset,
});
